import {Formik, Form} from 'formik'
import * as Yup from 'yup'
import FormikControl from '../../../components/formikControl/FormikControl'
import toast from 'react-hot-toast'
import {createDriver, updateDriver} from './api'
import {Col, Row} from 'react-bootstrap'
import {getCountryCallingCode, isPossiblePhoneNumber} from 'react-phone-number-input'
import {startOfToday} from 'date-fns'
import {handleApiError} from '../../../helpers/errorHandler'
import FormResetBtn from '../../../components/FormResetBtn'
import FormSubmitBtn from '../../../components/FormSubmitBtn'
import CountrySelect from '../../../components/CountryDropdown'

function DriverForm({initialValues, onAfterSubmit}) {
  const formType = initialValues ? 'update' : 'add'

  const submitHandler = (values, {setSubmitting: setLoading}) => {
    setLoading(true)
    const toastId = toast.loading('Submitting')
    const action = formType === 'add' ? createDriver : updateDriver

    const payload = {...values}
    payload.mobile = values.mobile.replace('+' + values.country_code, '')

    const dateFields = ['license_expiry_date', 'passport_expiry', 'qatar_id_expiry']
    dateFields.forEach((field) => {
      if (payload[field] === '') {
        payload[field] = null
      }
    })
    action(payload)
      .then(() => {
        toast.success('Success', {id: toastId})
        onAfterSubmit()
      })
      .catch((err) => {
        handleApiError(err, toastId)
      })
      .finally(() => setLoading(false))
  }
  let preSelectedVehicleTypes = []
  if (formType === 'update') {
    initialValues.name = initialValues.driver_name
    initialValues.mobile = '+' + initialValues.country_code + initialValues.driver_phone
    initialValues.user_status = initialValues.driver_status
    initialValues.userId = initialValues.user_id
    preSelectedVehicleTypes = initialValues.authorized_vehicle
      ? initialValues.authorized_vehicle?.map((selectedVehicle) =>
          [
            {label: 'Heavy Vehicle Duty (HVD)', value: 'hvd'},
            {label: 'Light Vehicle Duty (LVD)', value: 'lvd'},
            {label: 'Medium Truck', value: 'medium'},
            {label: 'Bus', value: 'bus'},
          ].find((vehicleType) => selectedVehicle === vehicleType.value)
        )
      : []
  }
  const formValues = initialValues || {
    name: '',
    email: '',
    mobile: '',
    country_code: '',
    user_status: '',
    gender: '',
    dob: '',
    country: '',
    whatsapp: '',
    driver_reg_num: '',
    blood_group: '',
    authorized_vehicle: '',
    license_expiry_date: '',
    passport: '',
    passport_expiry: '',
    passport_country: '',
    qatar_id: '',
    qatar_id_expiry: '',
  }

  // if (isFetching) {
  //   return <RunnerBall />
  // }

  return (
    <div className="px-5 py-3">
      <Formik
        initialValues={formValues}
        validationSchema={Yup.object({
          name: Yup.string()
            .max(100, 'The Driver name cannot exceed 100 characters')
            .required('Driver Name is required')
            .notOneOf([''], 'Driver Name is Required'),
          email: Yup.string()
            .matches(
              /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/,
              'Please enter a valid email address'
            )
            .email('Please enter a valid email address')
            .nullable(),
          mobile: Yup.string()
            .trim()
            .required('Mobile Number is required')
            .test('mobile', 'Enter a valid phone number', (value) => {
              if (value) {
                return isPossiblePhoneNumber(value)
              }
              return false
            })
            .notOneOf([''], 'Mobile Number is Required'),
          country_code: Yup.string()
            .trim()
            .required('Country Code is required')
            .notOneOf([''], 'Country Code is Required'),
          user_status: Yup.string().required('Status is required'),
          gender: Yup.string().nullable(),
          dob: Yup.date()
            .test('is-4-digit-year', 'Year must be a 4-digit number', (value) => {
              if (!value) return true
              const year = value.getFullYear()
              return year >= 1000 && year <= 9999
            })
            .nullable(),
          country: Yup.string().nullable(),
          blood_group: Yup.string().nullable(),
          whatsapp: Yup.string().nullable(),
          driver_reg_num: Yup.string().nullable(),
          authorized_vehicle: Yup.array().nullable(),
          license_expiry_date: Yup.date()
            .min(startOfToday(), 'License Expiry cannot be in the past')
            .nullable(),

          passport: Yup.string().nullable(),
          passport_expiry: Yup.date()
            .min(startOfToday(), 'Passport Expiry cannot be in the past')
            .nullable(),

          passport_country: Yup.string().nullable(),
          qatar_id: Yup.string().nullable(),
          qatar_id_expiry: Yup.date()
            .min(startOfToday(), 'Qatar Expiry Date cannot be in the past')
            .nullable(),
        })}
        onSubmit={submitHandler}
      >
        {({isSubmitting, setFieldValue, resetForm, dirty, isValid, values}) => (
          <Form>
            <Row>
              <Col md="4">
                <FormikControl
                  control="input"
                  required
                  placeholder="Driver Name"
                  label="Driver Name"
                  name="name"
                />
              </Col>
              <Col md="4">
                <FormikControl
                  control="phone-input"
                  required
                  placeholder="Mobile Number"
                  label="Mobile Number"
                  name="mobile"
                  onCountryChange={(value) => {
                    let code
                    if (value) {
                      code = getCountryCallingCode(value)
                    } else {
                      code = ''
                    }
                    setFieldValue('country_code', code)
                  }}
                />
              </Col>
              <Col md="4">
                <FormikControl
                  control="check"
                  type="radio"
                  options={[
                    {key: 'Active', value: '1'},
                    {key: 'Inactive', value: '0'},
                  ]}
                  required
                  placeholder="Active Status"
                  label="Active Status"
                  name="user_status"
                />
              </Col>
              <Col md="4">
                <FormikControl
                  control="input"
                  type="email"
                  placeholder="Email"
                  label="Email"
                  name="email"
                />
              </Col>
              <Col md="4">
                <FormikControl
                  control="check"
                  type="radio"
                  options={[
                    {key: 'Male', value: 'male'},
                    {key: 'Female', value: 'female'},
                  ]}
                  placeholder="Gender"
                  label="Gender"
                  name="gender"
                />
              </Col>
              <Col md="4">
                <FormikControl
                  control="input"
                  type="date"
                  placeholder="Date of Birth"
                  label="Date of Birth"
                  name="dob"
                />
              </Col>
              <Col md="4">
                <CountrySelect placeholder="Home Country" label="Home Country" name="country" />
              </Col>
              <Col md="4">
                <FormikControl
                  control="input"
                  type="number"
                  placeholder="Whatsapp Number"
                  label="Whatsapp Number"
                  name="whatsapp"
                />
              </Col>
              <Col md="4">
                <FormikControl
                  control="input"
                  placeholder="Employee ID"
                  label="Employee ID"
                  name="driver_reg_num"
                />
              </Col>
              <Col md="4">
                <FormikControl
                  control="select"
                  placeholder="Blood Group"
                  label="Blood Group"
                  name="blood_group"
                >
                  <option value="">Select Blood Group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </FormikControl>
              </Col>
              {/* <Col md="4">
                <FormikControl
                  control="textarea"
                  required
                  placeholder="Address"
                  label="Address"
                  name="address"
                />
              </Col> */}
              {/* <Col md="4">
                <FormikControl
                  control="select"
                  placeholder="Bus"
                  required
                  label="Bus"
                  name="bus_id"
                >
                  <option value="">Select Bus</option>
                  {data?.data?.map((bus) => (
                    <option value={bus.bus_id}>{bus.bus_name}</option>
                  ))}
                </FormikControl>
              </Col> */}

              <Col md="4">
                <FormikControl
                  control="select-advanced"
                  placeholder="Authorized Vehicle"
                  label="Authorized Vehicle"
                  name="authorized_vehicle"
                  options={[
                    {label: 'Heavy Vehicle Duty (HVD)', value: 'hvd'},
                    {label: 'Light Vehicle Duty (LVD)', value: 'lvd'},
                    {label: 'Medium Truck', value: 'medium'},
                    {label: 'Bus', value: 'bus'},
                  ]}
                  value={
                    values.authorized_vehicle
                      ? values.authorized_vehicle?.map((selectedVehicle) =>
                          [
                            {label: 'Heavy Vehicle Duty (HVD)', value: 'hvd'},
                            {label: 'Light Vehicle Duty (LVD)', value: 'lvd'},
                            {label: 'Medium Truck', value: 'medium'},
                            {label: 'Bus', value: 'bus'},
                          ].find((vehicleType) => selectedVehicle === vehicleType.value)
                        )
                      : []
                  }
                  optionValue="value"
                  optionLabel="label"
                  isMulti
                />
              </Col>

              <Col md="4">
                <FormikControl
                  control="input"
                  type="date"
                  placeholder="License Expiry Date"
                  label="License Expiry Date"
                  name="license_expiry_date"
                />
              </Col>

              <Col md="4">
                <FormikControl control="input" placeholder="Q ID" label="Q ID" name="qatar_id" />
              </Col>
              <Col md="4">
                <FormikControl
                  control="input"
                  type="date"
                  placeholder="Q ID Expiry Date"
                  label="Q ID Expiry Date"
                  name="qatar_id_expiry"
                />
              </Col>
              <Col md="4">
                <FormikControl
                  control="input"
                  placeholder="Passport Number"
                  label="Passport Number"
                  name="passport"
                />
              </Col>
              <Col md="4">
                <FormikControl
                  control="input"
                  type="date"
                  placeholder="Passport Expiry Date"
                  label="Passport Expiry Date"
                  name="passport_expiry"
                />
              </Col>
              <Col md="4">
                <CountrySelect
                  placeholder="Country"
                  label="Passport Issued Country"
                  name="passport_country"
                  value={{label: values.passport_country, value: values.passport_country}} // Adjusted for compatibility
                />
              </Col>
              <div className="text-center pt-10">
                <FormResetBtn onClick={() => resetForm} disabled={isSubmitting} className="me-4" />

                <FormSubmitBtn disabled={!dirty || !isValid || isSubmitting} />
              </div>
            </Row>
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default DriverForm
